<template>
    <hts-drawer
        v-model="visible"
        :title="$t('labels.viewSettingMarketplace')"
        :size="70"
        @close="close">
        <div
            v-if="isLoading"
            class="flex justify-center items-center h-64">
            <hts-loading />
        </div>
        <div
            v-else
            class="p-4">
            <div class="grid grid-cols-1 gap-4">
                <hts-form-select
                    v-model="formData.shipmentKey"
                    label="Loại hình vận chuyển"
                    :items="shipmentTypes"
                    key-id="key"
                    key-label="label"
                    name="shipmentType"
                    prop="shipmentType"
                    :clearable="false"
                    readonly />

                <hts-select-work-flow
                    v-model="formData.workflow"
                    v-model:name="formData.workflowName"
                    prop="workflow"
                    :transportation="formData.transportation"
                    :disabled="!formData.transportation"
                    :param-prepare="formData.shipmentKey"
                    readonly />

                <div
                    v-if="formData.positions.length > 0"
                    v-loading="loadingSelectNode"
                    class="space-y-4">
                    <!-- Tiêu đề -->
                    <span class="text-lg font-semibold text-default-700">
                        {{ $t('labels.route') }}
                    </span>

                    <p>
                        Cài đặt điểm bắt đầu, điểm kết thúc trong chặng của khách hàng
                    </p>

                    <!-- Danh sách tuyến -->
                    <div class="overflow-x-auto pb-2">
                        <div
                            :style="{ minWidth: minWidthScroll }"
                            class="space-y-4">
                            <div
                                v-for="(item, index) in formData.positions"
                                :key="index"
                                :class="['bg-white rounded-xl shadow-sm p-4 transition']">
                                <div class="flex items-center justify-between gap-4">
                                    <!-- Tên node -->
                                    <div class="text-base font-medium text-gray-700 flex-1">
                                        {{ index + 1 }}. {{ item.nodeTypeName }}
                                    </div>

                                    <div class="flex gap-2">
                                        <hts-form-button
                                            size="small"
                                            color="info"
                                            type="button"
                                            :outline="formData.selectedStartIndex !== index">
                                            {{ formData.selectedStartIndex === index ? '✅ Bắt đầu' : 'Chọn bắt đầu' }}
                                        </hts-form-button>

                                        <hts-form-button
                                            size="small"
                                            color="success"
                                            type="button"
                                            :outline="formData.selectedEndIndex !== index">
                                            {{ formData.selectedEndIndex === index ? '✅ Kết thúc' : 'Chọn kết thúc' }}
                                        </hts-form-button>
                                    </div>

                                    <hts-form-button
                                        icon-name="hi-pencil-square-icon"
                                        size="small"
                                        class="min-w-[12rem]"
                                        color="info"
                                        :loading="updateCompletionTime"
                                        @click="() => openWorkflowPosition(item)">
                                        {{ $t('labels.editCompletionTime') }}
                                    </hts-form-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        v-if="formData.selectedStartIndex !== null && formData.selectedEndIndex !== null"
                        class="text-green-700 font-medium mt-2">
                        Bạn đã chọn từ điểm {{ formData.selectedStartIndex + 1 }} đến điểm {{ formData.selectedEndIndex + 1 }}.
                    </div>
                </div>

                <!-- Thiết lập dung sai -->
                <div class="mt-4">
                    <span class="text-lg font-semibold text-default-700">
                        Thiết lập dung sai KM
                    </span>
                    <p>
                        Khoảng chênh lệch quãng đường cho phép giữa khách hàng và thực tế nhà xe.
                    </p>

                    <hts-form-select
                        v-model="formData.toleranceType"
                        :items="[
                            { label: 'Dung sai cố định (km)', value: 'km' },
                            { label: 'Dung sai theo %', value: 'percent' }
                        ]"
                        label="Loại dung sai"
                        name="toleranceType"
                        prop="toleranceType"
                        :clearable="false" />

                    <!-- Dung sai theo km -->
                    <hts-form-input
                        v-if="formData.toleranceType === 'km'"
                        v-model="formData.toleranceKm"
                        type="number"
                        label="Dung sai cố định (km)"
                        placeholder="Nhập số km"
                        :min="0"
                        readonly />

                    <!-- Dung sai theo % -->
                    <hts-form-input
                        v-else-if="formData.toleranceType === 'percent'"
                        v-model="formData.tolerancePercent"
                        type="number"
                        label="Dung sai theo %"
                        placeholder="Nhập phần trăm"
                        :min="0"
                        :max="1000"
                        readonly />
                </div>

                <workflow-position
                    ref="workflowPosition"
                    @update="(val) => updateWorkflowPosition(val)" />
            </div>
        </div>
    </hts-drawer>
</template>

<script setup lang="ts">
import WorkflowPosition from './workflow-position.vue'
import screen from '~/components/utils/screen'
import shipmentTypes from '~/data/select/shipmentAndTransportationTypes.json'
import type { IWorkflowPosition } from '~/pages/admincp/workflow/-imports/form'
const visible = ref<boolean>(false)
const breakpoint = screen()
const minWidthScroll = computed(() => {
  return `${breakpoint.value.thresholds.sm}px`
})
const { $disableReactive, $t } = useNuxtApp()

const dataWorkflow = ref<{ [key: string]: any }>({})
const loadingSelectNode = ref<boolean>(false)
const updateCompletionTime = ref(false)
const workflowPosition = ref()

const formData = ref<{ [key: string]: any }>({
  shipmentKey: '',
  shipmentTypeName: '',
  transportation: '',
  shipmentType: '',

  selectedStartIndex: null,
  selectedEndIndex: null,

  workflow: '',
  workflowName: '',
  positions: [],

  toleranceType: 'km',
  toleranceKm: 0,
  tolerancePercent: 0
  // commodity: [],
  // typeCommodity: '',
  // typeReefer: '',
  // goodType: '',
  // typeDangerous: '',
  // typeFragileGoods: '',
  // weightCommodity: null,
  // contCategory: '',
  // vehicleCategory: '',
  // vehicleCategoryName: '',
  // quote: '',
  // productivity: null,

  // theSamePositions: []
})

const isLoading = ref(false)
const isSubmitting = ref(false)
const settingId = ref('')

const emits = defineEmits(['refreshTables'])

const updateWorkflowPosition = (position: IWorkflowPosition) => {
  const index = position.index - 1
  formData.value.positions[index] = position
}

function selectStart (index: number) {
  formData.value.selectedStartIndex = index
  formData.value.selectedEndIndex = null
}

const openWorkflowPosition = (position: IWorkflowPosition) => {
  if (workflowPosition.value) {
    workflowPosition.value.open($disableReactive(position), dataWorkflow.value.transportation, true)
  }
}

/**
 * Chọn điểm kết thúc (chỉ cho phép sau điểm bắt đầu)
 */
function selectEnd (index: number) {
  if (formData.value.selectedStartIndex !== null && index > formData.value.selectedStartIndex) {
    formData.value.selectedEndIndex = index
  } else {
    $toast().error('Vui lòng chọn điểm kết thúc nằm sau điểm bắt đầu')
  }
}

const fetchData = async (id: string) => {
  isLoading.value = true
  try {
    const api = {
      url: '/marketplace/setting/ID',
      method: 'get'
    }
    const response = await $api(api, { uriParams: { ID: id } })
    const resData = response?.data?.value

    if (resData?.success) {
      const data = resData.data

      formData.value = data
    }
  } catch (error) {
    process.client && $toast().error($t('labels.errorFetchingData'))
  } finally {
    isLoading.value = false
  }
}

const open = (id: string) => {
  settingId.value = id
  // resetForm()
  fetchData(id)
  visible.value = true
}

const close = () => {
  visible.value = false
}

defineExpose({
  open
})
</script>
